import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { CheckSmallIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import '@/shared/features/classroom/constants/events.quiz-classroom-point';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { AXIOS_INSTANCE } from '@/api/httpClient';
import { CaretDownIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { type AvatarShape, QUERY_KEY_AVATAR_CATALOG, useAvatarCatalog } from '@/shared/features/user-avatar';
import { alertDialog, Button, confirmDialog } from '@/ui_v3';
import { type SelectOption, SingleSelect } from '@/ui_v3/components/select';
import { cn } from '@/utils';
import { applyPartSelection, buildGridItems, type CategoryTab, findShapeByPartId, getCategoryConfig, getNextAccessoryId, getSelectedPartId, getVariantPartIdForShape, type PartSortKey, sanitizeUnownedParts, shouldShowColorPicker, sortGridItems } from '../avatarDecorate.logic';
import { type AvatarEventSource } from '../events.quiz-profile-avatar';
import { QUERY_KEY_MY_POINTS, useMyPoints } from '../hooks/useMyPoints';
import { type AvatarDecorateForm } from '../schema';
import { AvatarCategoryTabs } from './AvatarCategoryTabs';
import { AvatarColorPicker } from './AvatarColorPicker';
import { AvatarItemGrid, AvatarItemGridSkeleton } from './AvatarItemGrid';
import { AVATAR_PURCHASE_COPY, AvatarPurchaseConfirmBody } from './AvatarPurchaseConfirmBody';
import { PURCHASE_TOAST_COPY, PurchaseToast } from './PurchaseToast';
const CONTROL_COPY = {
  owned: {
    key: 'common:avatarDecorate.ownedOnlyFilter'
  },
  sortDefault: {
    key: 'common:avatarDecorate.sortRecommended'
  },
  sortLatest: {
    key: 'common:avatarDecorate.sortLatest'
  },
  sortPriceAsc: {
    key: 'common:avatarDecorate.sortPriceAsc'
  },
  sortPriceDesc: {
    key: 'common:avatarDecorate.sortPriceDesc'
  }
} as const;
const LOGIN_REQUIRED_COPY = {
  title: {
    key: 'common:avatarDecorate.loginRequiredTitle'
  },
  body: {
    key: 'common:avatarDecorate.loginRequiredBody'
  },
  action: {
    key: 'common:avatarDecorate.loginAction'
  }
} as const;

// Record<PartSortKey, { key: string }> 로 넓히면 key 가 string 이 돼 t() 의
// 타입드 키 검사를 못 받는다. satisfies 로 리터럴 타입을 유지한다.
const SORT_OPTION_COPY = {
  default: CONTROL_COPY.sortDefault,
  latest: CONTROL_COPY.sortLatest,
  priceAsc: CONTROL_COPY.sortPriceAsc,
  priceDesc: CONTROL_COPY.sortPriceDesc
} as const satisfies Record<PartSortKey, {
  key: string;
}>;
const CATEGORY_PRICE_LABEL_COPY = {
  face: {
    key: 'common:avatarDecorate.purchaseFacePriceLabel'
  },
  skin: {
    key: 'common:avatarDecorate.purchaseSkinPriceLabel'
  },
  hair: {
    key: 'common:avatarDecorate.purchaseHairPriceLabel'
  },
  clothes: {
    key: 'common:avatarDecorate.purchaseClothesPriceLabel'
  },
  glasses: {
    key: 'common:avatarDecorate.purchaseGlassesPriceLabel'
  }
} as const satisfies Record<CategoryTab, {
  key: string;
}>;

/**
 * 정렬 Select 의 커스텀 트리거 (Figma 1506:33351 — 140×32).
 *
 * 공용 `SelectTrigger` 는 intent 별로 높이가 48/58px 로 고정이고, `SingleSelectUI` 가
 * className 을 `w-full` 로 덮어써서 props 로는 시안 치수를 만들 수 없다.
 * `trigger` prop 이 트리거를 통째로 교체하는 공식 경로라 이걸 쓴다.
 *
 * Radix 가 asChild 로 `data-state` 를 내려주므로 열림 상태 스타일은 그걸로 잡는다.
 */
const SortSelectTrigger = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<'button'> & {
  label: string;
}>(({
  label,
  ...props
}, ref) => <button ref={ref} type="button" {...props} className="flex h-[32px] w-[140px] shrink-0 cursor-pointer select-none items-center justify-between gap-4xs rounded-sm border border-alternative px-sm text-body-sm-medium text-default data-[state=open]:border-transparent data-[state=open]:ring-2 data-[state=open]:ring-inset data-[state=open]:ring-strong-default">
    <span className="truncate">{label}</span>
    <CaretDownIcon size="sm" fill={ICON_COLOR.default} className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
  </button>);
SortSelectTrigger.displayName = 'SortSelectTrigger';

// 구매 카드 획득 연출의 총 길이(팝 360 / 링 500 / 배지 120+420). 가장 긴 것에 맞춰 비운다.
const PURCHASE_FLASH_MS = 700;
export const AvatarPartSelector = ({
  insufficientPointHelpHref,
  source
}: {
  insufficientPointHelpHref?: string;
  source: AvatarEventSource;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    setValue,
    getValues,
    watch
  } = useFormContext<AvatarDecorateForm>();
  const router = useRouter();
  const {
    data: catalog,
    isLoading: isCatalogLoading
  } = useAvatarCatalog('quiz');
  const queryClient = useQueryClient();
  const {
    user
  } = useUser();
  const hidePointUi = !!user && user.type?.toLowerCase() !== 'student';
  // 구매 전 잔액 사전 확인용(비학생은 훅 내부에서 enabled=false 라 호출되지 않는다).
  const {
    data: myPoints,
    isSuccess: isMyPointsLoaded
  } = useMyPoints();
  const [activeTab, setActiveTab] = useState<CategoryTab>('skin');
  const [selectedShape, setSelectedShape] = useState<AvatarShape | null>(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const purchaseMutation = useMutation({
    mutationFn: async (shapeId: number) => {
      await AXIOS_INSTANCE.post(`/avatar/shapes/${shapeId}/purchase`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_AVATAR_CATALOG, 'quiz', user?.quizUserId]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MY_POINTS, user?.quizUserId]
      });
    }
  });
  const watchAvatar = watch('avatar');
  const cfg = getCategoryConfig(activeTab);
  const shapes = useMemo<AvatarShape[]>(() => catalog?.shapes[cfg.shapeKey] ?? [], [catalog, cfg.shapeKey]);
  const gridItems = useMemo(() => buildGridItems(shapes, cfg.shapeKey), [shapes, cfg.shapeKey]);
  const [showOwnedOnly, setShowOwnedOnly] = useState(false);
  const [sortKey, setSortKey] = useState<PartSortKey>('default');
  // 방금 구매한 shape — 그 카드에만 획득 연출을 얹는다. 연출이 끝나면 다시 null 로 비운다.
  const [justPurchasedShapeId, setJustPurchasedShapeId] = useState<number | null>(null);
  const purchaseFlashTimer = useRef<ReturnType<typeof setTimeout>>();
  /** 확인 모달을 띄운 시점부터 구매 응답까지 아이템별로 중복 요청을 잠근다. */
  const purchasingShapeIds = useRef(new Set<number>());
  const flashPurchase = (shapeId: number) => {
    if (purchaseFlashTimer.current) clearTimeout(purchaseFlashTimer.current);
    // 같은 카드를 재구매(사실상 불가)하거나 연속 구매해도 연출이 재시작되도록 먼저 비운다.
    setJustPurchasedShapeId(null);
    // 다음 페인트에 클래스가 새로 붙어 애니메이션이 처음부터 재생된다.
    requestAnimationFrame(() => setJustPurchasedShapeId(shapeId));
    purchaseFlashTimer.current = setTimeout(() => setJustPurchasedShapeId(null), PURCHASE_FLASH_MS);
  };
  useEffect(() => () => clearTimeout(purchaseFlashTimer.current), []);
  const sortOptions = useMemo<SelectOption<PartSortKey>[]>(() => {
    // 비학생은 가격 UI 자체가 없어(hidePointUi) 가격순 정렬이 의미가 없다.
    const keys: PartSortKey[] = hidePointUi ? ['default', 'latest'] : ['default', 'latest', 'priceAsc', 'priceDesc'];
    return keys.map(key => ({
      value: key,
      label: t(SORT_OPTION_COPY[key].key)
    }));
  }, [hidePointUi, t]);
  const visibleGridItems = useMemo(() => {
    const filtered = showOwnedOnly ? gridItems.filter(item => !item.isLocked) : gridItems;
    return sortGridItems(filtered, sortKey);
  }, [gridItems, showOwnedOnly, sortKey]);
  useEffect(() => {
    setShowOwnedOnly(false);
  }, [activeTab]);

  // avatar 값/탭 변경 시 선택 shape·color 동기화
  useEffect(() => {
    if (!catalog) return;
    const id = getSelectedPartId(watchAvatar, activeTab);
    const result = findShapeByPartId(shapes, id);
    if (result) {
      setSelectedShape(result.shape);
      setSelectedColorIndex(result.colorIndex);
    } else {
      setSelectedShape(null);
      setSelectedColorIndex(0);
    }
  }, [watchAvatar, activeTab, catalog, shapes]);

  // 미구매 유료 아이템을 입고 있으면 무료 아이템으로 되돌린다.
  // shouldDirty 를 주지 않아 '저장' 이 임의로 활성화되지는 않는다.
  useEffect(() => {
    if (!catalog) return;
    const current = getValues('avatar');
    const sanitized = sanitizeUnownedParts(current, catalog);
    if (sanitized === current) return;
    setValue('avatar', sanitized);
  }, [catalog, getValues, setValue]);
  const applySelectedPart = (gridId: number, shape: AvatarShape | undefined) => {
    const avatar = getValues('avatar');
    let nextId = gridId;
    if (shape) {
      setSelectedShape(shape);
      const variant = getVariantPartIdForShape(shape, selectedColorIndex);
      if (variant) {
        setSelectedColorIndex(variant.colorIndex);
        nextId = variant.partId;
      }
    }
    if (cfg.accessorySlot) {
      const currentId = getSelectedPartId(avatar, activeTab);
      nextId = getNextAccessoryId(currentId, nextId);
      if (nextId === 0) setSelectedShape(null);
    }
    setValue('avatar', applyPartSelection(avatar, activeTab, nextId), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };
  const handleSelectPart = async (gridId: number) => {
    const absPreviewId = Math.abs(gridId);
    const shape = shapes.find(s => s.previewPartId === absPreviewId);
    if (shape?.usable === false) {
      if (!user) {
        // 유료 아이템은 로그인해야 살 수 있다. 어떤 아이템에서 막혔는지까지 남겨야
        // '무엇이 게스트를 로그인시키는가'를 아이템 단위로 볼 수 있다.
        const loginWallEventProps = {
          price: shape.price ?? 0,
          category: activeTab,
          shape_id: shape.id
        } as const;
        trackEvent('quiz_point_purchase_login_required_view', loginWallEventProps);
        const shouldLogin = await confirmDialog.openAsync({
          size: 'xs',
          forceDrawerOnMobile: true,
          header: {
            title: t(LOGIN_REQUIRED_COPY.title.key)
          },
          body: t(LOGIN_REQUIRED_COPY.body.key),
          cancelText: t('common:global.close.button'),
          confirmText: t(LOGIN_REQUIRED_COPY.action.key)
        });
        // 닫기(dismiss)도 남긴다. 노출만 있고 선택이 없으면
        // '안 눌렀다' 와 '아직 고민중' 을 구분할 수 없다.
        trackEvent('quiz_point_purchase_login_required_cta_click', {
          ...loginWallEventProps,
          destination: shouldLogin ? 'login' : 'dismiss'
        });
        if (shouldLogin) {
          trackEvent('quiz_profile_avatar_login_click', {
            source
          });
          const callbackUrl = new URL(router.asPath, window.location.origin);
          callbackUrl.searchParams.set('avatar_login', '1');
          callbackUrl.searchParams.set('avatar_login_source', source);
          void router.push({
            pathname: '/login',
            query: {
              callbackUrl: `${callbackUrl.pathname}${callbackUrl.search}`
            }
          });
        }
        return;
      }
      if (user.type?.toLowerCase() === 'student') {
        // 구매 요청이 떠 있는 동안엔 잠긴 아이템 클릭을 무시한다.
        // 확인 모달이 닫힌 뒤에도 응답 전까지 카드는 여전히 잠금으로 보여서 다시 누르기 쉬운데,
        // 서버가 409(AVATAR_SHAPE_ALREADY_OWNED)로 막아 이중 차감은 없지만
        // 방금 산 아이템에 '구매 실패' 알럿이 뜬다.
        // isPending 은 mutate 이후에야 true 라, 확인 모달이 뜨는 사이 두 번 누르면
        // 모달이 두 개 열려 같은 아이템을 두 번 산다. 아이템별로 직접 잠근다.
        if (purchaseMutation.isPending || purchasingShapeIds.current.has(shape.id)) return;

        // 포인트가 모자라면 구매 창을 띄우지 않고 곧바로 안내한다.
        // (서버 실패 후 '구매할 수 없어요' 로 알리면 학생이 이유를 알기 어렵다)
        const price = shape.price ?? 0;
        const balance = myPoints?.currentBalance ?? 0;
        const purchaseEventProps = {
          price,
          category: activeTab,
          shape_id: shape.id
        } as const;
        const gridItem = gridItems.find(item => item.id === gridId);
        const bodyProps = {
          previewUrl: gridItem?.pathName ?? '',
          colorParts: activeTab === 'skin' ? undefined : shape.parts,
          price,
          balance,
          itemPriceLabel: String(t(CATEGORY_PRICE_LABEL_COPY[activeTab].key)),
          isNew: gridItem?.isNew,
          isHot: gridItem?.isHot
        };

        // 잔액이 확실히 로드된 경우에만 사전 '포인트 부족' 판정을 한다.
        // 미로드 상태(myPoints undefined -> balance 0)로 오판정해 구매를 막지 않기 위함.
        // balance/price/로드상태를 남겨 stale 0 또는 미로드로 인한 오판정을 관측한다.
        logger.debug('avatar_purchase_precheck', {
          shapeId: shape.id,
          price,
          balance,
          isMyPointsLoaded,
          blocked: isMyPointsLoaded && balance < price
        });
        if (isMyPointsLoaded && balance < price) {
          trackEvent('quiz_point_purchase_insufficient_view', {
            ...purchaseEventProps,
            balance,
            current_jelly_balance: balance
          });
          // 구매 모달과 같은 레이아웃을 쓰되 확인 버튼 하나만 둔다.
          // 살 수 없는 상태에서 '구매' 를 눌리게 두면 안 되기 때문.
          alertDialog.open({
            size: 'sm',
            forceDrawerOnMobile: true,
            header: {
              title: t(AVATAR_PURCHASE_COPY.insufficientTitle.key)
            },
            body: <AvatarPurchaseConfirmBody {...bodyProps} isInsufficient insufficientHelpHref={insufficientPointHelpHref} onInsufficientHelpClick={() => trackEvent('quiz_point_purchase_insufficient_classroom_click', {
              ...purchaseEventProps,
              current_jelly_balance: balance,
              destination: 'classroom_list'
            })} />,
            confirmText: t('common:global.confirm.button'),
            confirmIntent: 'neutral'
          });
          return;
        }
        trackEvent('quiz_point_purchase_view', purchaseEventProps);
        purchasingShapeIds.current.add(shape.id);
        const confirmed = await confirmDialog.openAsync({
          size: 'sm',
          forceDrawerOnMobile: true,
          header: {
            title: t(AVATAR_PURCHASE_COPY.confirmTitle.key)
          },
          body: <AvatarPurchaseConfirmBody {...bodyProps} />,
          cancelText: t('common:global.cancel.button'),
          confirmText: t(AVATAR_PURCHASE_COPY.purchaseAction.key)
        });
        if (!confirmed) {
          purchasingShapeIds.current.delete(shape.id);
          return;
        }
        trackEvent('quiz_point_purchase_submit', purchaseEventProps);
        try {
          await purchaseMutation.mutateAsync(shape.id);
        } catch {
          trackEvent('quiz_point_purchase_fail', {
            ...purchaseEventProps,
            reason: 'purchase_error'
          });
          await alertDialog.openAsync({
            forceDrawerOnMobile: true,
            header: {
              title: t('common:avatarDecorate.purchaseFailTitle')
            },
            body: t('common:avatarDecorate.purchaseFailBody'),
            confirmText: t('common:global.confirm.button')
          });
          return;
        } finally {
          purchasingShapeIds.current.delete(shape.id);
        }

        // applySelectedPart 와 같은 규칙으로 '실제 적용될 파츠 id' 를 구한다.
        // 구매(shape.id)와 아바타 저장(partId)이 다른 체계라, 이 값이 있어야
        // 산 아이템을 실제로 입었는지 저장 이벤트와 이어서 볼 수 있다.
        trackEvent('quiz_point_purchase_success', {
          ...purchaseEventProps,
          part_id: Math.abs(getVariantPartIdForShape(shape, selectedColorIndex)?.partId ?? gridId)
        });
        applySelectedPart(gridId, {
          ...shape,
          owned: true,
          usable: true
        });
        // 구매 성공 — 그 카드에 획득 연출 + 확인 토스트.
        flashPurchase(shape.id);
        toast.custom(toastItem => <PurchaseToast isVisible={toastItem.visible} message={String(t(PURCHASE_TOAST_COPY.title.key))} />, {
          position: 'top-center',
          duration: 3000
        });
        return;
      }
    }
    applySelectedPart(gridId, shape);
  };

  /** 액세서리 '없음' 타일: 해당 슬롯을 0(미착용)으로 비운다. */
  const handleSelectNone = () => {
    setSelectedShape(null);
    setValue('avatar', applyPartSelection(getValues('avatar'), activeTab, 0), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };
  const handleSelectColor = (colorIndex: number) => {
    setSelectedColorIndex(colorIndex);
    if (!selectedShape) return;
    const variant = getVariantPartIdForShape(selectedShape, colorIndex);
    if (!variant) return;
    const avatar = getValues('avatar');
    setValue('avatar', applyPartSelection(avatar, activeTab, variant.partId), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };
  return <div className="flex h-full flex-col overflow-hidden mobile-lg:flex-row" data-sentry-component="AvatarPartSelector" data-sentry-source-file="AvatarPartSelector.tsx">
      <div className="shrink-0 overflow-x-auto border-b border-assistive px-sm pt-xs mobile-lg:w-[140px] mobile-lg:overflow-visible mobile-lg:border-b-0 mobile-lg:border-r mobile-lg:border-alternative mobile-lg:px-0 mobile-lg:pt-0">
        <AvatarCategoryTabs activeTab={activeTab} onSelect={setActiveTab} data-sentry-element="AvatarCategoryTabs" data-sentry-source-file="AvatarPartSelector.tsx" />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden mobile-lg:w-[520px] mobile-lg:flex-initial">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto mobile-lg:overflow-hidden">
          {/*
            모바일에서는 컨트롤 행도 아이템 목록과 함께 위로 밀려나야 한다.
            desktop/mobile-lg 이상에서는 이 래퍼의 overflow 를 막고 그리드만 스크롤한다.
           */}
          {!isCatalogLoading &&
        // Figma 1506:31790 — 좌측 정렬 Select / 우측 '보유 중' 필터.
        // 배경은 아래로 갈수록 투명해지는 흰 그라디언트(#fff 90% → transparent):
        // 데스크탑에서 그리드가 밑으로 스크롤될 때 컨트롤 행 하단에서 부드럽게 비친다.
        <div className="flex shrink-0 items-start justify-between gap-xs bg-[linear-gradient(180deg,#fff_90%,rgba(255,255,255,0)_100%)] px-md pb-2xs pt-md">
              <SingleSelect<PartSortKey> value={sortKey} options={sortOptions}
          // 같은 값을 다시 고르면 useSingleSelect 가 null 을 넘긴다 → 추천순으로 되돌린다.
          onValueChange={value => setSortKey(value ?? 'default')} trigger={<SortSelectTrigger label={t(SORT_OPTION_COPY[sortKey].key)} />} />
              {/* 비학생은 전 아이템이 열려 있어 '보유 중' 이 아무것도 걸러내지 못한다. */}
              {!hidePointUi && <Button variant="outlined" intent="neutral" size="xs" rounded="full" aria-pressed={showOwnedOnly} onClick={() => setShowOwnedOnly(prev => !prev)} className={cn('shrink-0', showOwnedOnly && 'border-strong bg-strong-alternative text-strong hover:!bg-strong-alternative')} leftSlot={<CheckSmallIcon size="xs" />}>
                  {t(CONTROL_COPY.owned.key)}
                </Button>}
            </div>}
          <div className="min-h-0 flex-1 bg-white px-sm pb-lg pt-sm mobile-lg:overflow-y-auto mobile-lg:px-md mobile-lg:pt-sm">
            {isCatalogLoading ? <AvatarItemGridSkeleton hidePointUi={hidePointUi} /> : <AvatarItemGrid items={visibleGridItems} selectedShapeId={selectedShape?.id ?? null} onSelect={handleSelectPart} hidePointUi={hidePointUi} showNoneOption={!!cfg.accessorySlot} isNoneSelected={!!cfg.accessorySlot && getSelectedPartId(watchAvatar, activeTab) === 0} onSelectNone={handleSelectNone} justPurchasedShapeId={justPurchasedShapeId} />}
          </div>
        </div>
        {shouldShowColorPicker(selectedShape) && selectedShape && <div className="relative z-[1] shrink-0 border-t border-assistive bg-white shadow-[0_1px_4px_0_rgba(28,27,31,0.12),0_0_6px_0_rgba(28,27,31,0.04)]">
            <AvatarColorPicker colors={selectedShape.parts} selectedColorIndex={selectedColorIndex} onSelect={handleSelectColor} />
          </div>}
      </div>
    </div>;
};